import type { SubmitEventHandler } from 'react';
import type { DropdownOption } from '../../../components/Dropdown/DropdownOption/DropdownOption';

import { use, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import z from 'zod';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { useAppForm } from '../../../form-hooks/form';
import { useUserSpaces } from '../../api/useUserSpaces';

interface SynchronizeSpaceModalContentProps {
  sourceSpaceId: string;
  onSubmit: (targetSpaceId: string) => Promise<boolean>;
  testId?: string;
}

const SynchronizeSpaceModalContent = ({
  sourceSpaceId,
  onSubmit,
  testId,
}: SynchronizeSpaceModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const { spaces, isLoading } = useUserSpaces();

  const spaceOptions: DropdownOption[] = useMemo(
    () =>
      (spaces ?? [])
        .filter((space) => space.id !== sourceSpaceId)
        .map((space) => ({ label: space.name, value: space.id })),
    [spaces, sourceSpaceId],
  );

  const hasTargets = !isLoading && spaceOptions.length > 0;

  const validation = useMemo(
    () =>
      z.object({
        targetSpaceId: z
          .string()
          .min(1, t('SpaceSynchronization.Synchronize.TargetRequired')),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: { targetSpaceId: '' },
    validators: { onChange: validation },
    onSubmit: async ({ value }) => {
      const ok = await onSubmit(value.targetSpaceId);
      if (ok) {
        modalInstance.resolve?.(true);
      }
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <div {...getTestProps(testId, 'synchronize-modal-container')}>
      <form.AppForm>
        <form
          id="synchronize-space-modal-form"
          className="w-full flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
          {...getTestProps(testId)}
        >
          <p className="mb-2">
            {t('SpaceSynchronization.Synchronize.Paragraph')}
          </p>
          <form.AppField name="targetSpaceId">
            {(field) => (
              <field.Dropdown
                label={t('SpaceSynchronization.Synchronize.SelectorLabel')}
                options={spaceOptions}
                disabled={!hasTargets}
                additionalClasses={'text-left'}
                required
                helpText={
                  !hasTargets
                    ? t('SpaceSynchronization.Synchronize.SelectorHelperText')
                    : undefined
                }
                {...getTestProps(testId, 'target-space', 'testId')}
              />
            )}
          </form.AppField>

          <div
            className={twMerge(
              'flex justify-center gap-3 absolute bottom-6 pt-5',
              'left-1/2 -translate-x-1/2 w-full',
              'border-t border-slate-100 dark:border-slate-800',
            )}
          >
            <Button
              buttonColor="blueBordered"
              type="button"
              onClick={() => modalInstance.resolve?.(false)}
              {...getTestProps(testId, 'cancel', 'testId')}
            >
              {t('Global.Cancel')}
            </Button>

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  buttonColor="blue"
                  type="submit"
                  disabled={!canSubmit || isSubmitting || !hasTargets}
                  {...getTestProps(testId, 'save', 'testId')}
                >
                  {t('SpaceSynchronization.Synchronize.Synchronize')}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default SynchronizeSpaceModalContent;
